import React, { useCallback } from 'react';

import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';

import { Button } from '@mui/material';

type AgentTableRow = {
  email: string;
  agentId: string;
  dayCounts: Array<{ day: number; count: number; date: string }>;
  total: number;
};

type WarningType = 'soft' | 'hard' | 'none' | null;

interface SmsVerifyStatsCsvExportProps {
  tableData: Array<AgentTableRow>;
  showWarningLevels?: boolean;
  warningLevels?: Map<string, WarningType>;
}

/**
 * Self-contained component for exporting SMS verification stats to CSV.
 * Handles CSV generation, file download, and user feedback.
 */
export default function SmsVerifyStatsCsvExport({
  tableData,
  showWarningLevels = false,
  warningLevels = new Map(),
}: SmsVerifyStatsCsvExportProps) {
  const { enqueueSnackbar } = useSnackbar();

  const handleExportCsv = useCallback(() => {
    if (!tableData || tableData.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    // Get dates for headers - use the first row's dates as reference
    const firstRow = tableData[0];
    const dateHeaders = firstRow.dayCounts.map((dc) => dc.date);

    // Build CSV header row
    const headers = ['Email', 'Agent ID', ...dateHeaders, 'Total'];
    if (showWarningLevels) {
      headers.splice(2, 0, 'Warning Level');
    }

    // Build CSV rows
    const rows = tableData.map((row) => {
      const warningLevel = warningLevels.get(row.email.toLowerCase()) || 'none';
      const rowData = [
        row.email,
        row.agentId || '',
        ...(showWarningLevels ? [warningLevel] : []),
        ...row.dayCounts.map((dc) => dc.count.toString()),
        row.total.toString(),
      ];
      return rowData;
    });

    // Convert to CSV format (escape commas and quotes)
    const escapeCsvValue = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvLines = [headers.map(escapeCsvValue).join(','), ...rows.map((row) => row.map(escapeCsvValue).join(','))];

    const csvContent = csvLines.join('\n');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const fileName = `sms-verification-stats-${timestamp}.csv`;

    fileDownload(csvContent, fileName);
    enqueueSnackbar('CSV exported successfully', { variant: 'success' });
  }, [tableData, showWarningLevels, warningLevels, enqueueSnackbar]);

  return (
    <Button variant="contained" color="primary" onClick={handleExportCsv}>
      Export CSV
    </Button>
  );
}
